/**
 * The classic frame for the familiar modes (theme spec S2.2): the same
 * routed page, nav model, commands and hosts as the modern frame, laid out
 * as title strip, menu bar, icon toolbar, module bar and patient strip from
 * the active mode's chrome spec (app/layout/skins/<id>.menus.ts).
 */
import { Link, useNavigate } from '@tanstack/react-router';
import { PRODUCT } from '@velora/contracts/product';
import type { ToolbarLayout as ToolbarLayoutValue } from '@velora/contracts/shell';
import { SKIN_LABELS } from '@velora/contracts/skins';
import {
  ClassicShell,
  IconToolbar,
  MenuBar,
  ModuleBar,
  PaletteTrigger,
  PatientStrip,
  StatusBar,
  TitleStrip,
  type NavLinkRenderProps,
} from '@velora/ui';
import { useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { counterValue, registerCounter, useCounterValue } from '../actions/counters.js';
import { notifications } from '../notifications/store.js';
import {
  actionRegistry,
  actionState,
  findAction,
  greysDisabledActions,
} from '../actions/registry.js';
import { commandRegistry } from '../commands/registry.js';
import { Icon } from '../icons.js';
import { useOfficeScope } from '../office/OfficeScope.js';
import { useCurrentPatient } from '../patient/current.js';
import { usePrivacy } from '../privacy/privacy.js';
import { canInScope } from '../session/access.js';
import { usePrefs } from '../prefs/prefs.js';
import { useSession } from '../session/session.js';
import { shortcutLabels } from '../shortcuts/keys.js';
import { isMacPlatform } from '../shortcuts/platform.js';
import { SkinSlot, useSkin, useSkinSlot, useTerms } from '../skins/SkinProvider.js';
import type {
  ClassicChrome,
  ChromeContext as SkinChromeContext,
  SkinIcon,
} from '../skins/types.js';
import {
  applyToolbarLayout,
  labelText,
  resolveMenuItems,
  resolveToolbar,
  splitModules,
  tileHref,
  titleText,
  type ChromeContext,
  type NavEntry,
  type ResolvedAction,
  type ResolvedToolbarButton,
} from './classicModel.js';
import { CustomizeToolbar } from './CustomizeToolbar.js';
import { AutomationsStatusText } from './AutomationsIndicator.js';
import { TimeclockStatusText } from './TimeclockButton.js';

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase())
    .slice(0, 3)
    .join('');
}

function renderLink({ href, className, children, ...rest }: NavLinkRenderProps) {
  return (
    <Link
      to={href}
      activeOptions={{ exact: true, includeSearch: false }}
      activeProps={{}}
      className={rest['aria-current'] ? `${className} active` : className}
      aria-current={rest['aria-current']}
    >
      {children}
    </Link>
  );
}

function renderSkinIcon(C: SkinIcon): ReactNode {
  return <C size={18} aria-hidden />;
}

export interface ClassicFrameProps {
  chrome: ClassicChrome;
  nav: readonly NavEntry[];
  activeId: string | undefined;
  activeModuleId: string | undefined;
  /** The page title (route title) for the title strip. */
  title: string;
  onPalette: () => void;
  /** Right end of the menu bar: office, freshness, notifications, user menu. */
  menuBarRight: ReactNode;
  children: ReactNode;
}

export function ClassicFrame({
  chrome,
  nav,
  activeId,
  activeModuleId,
  title,
  onPalette,
  menuBarRight,
  children,
}: ClassicFrameProps) {
  const skin = useSkin();
  const t = useTerms();
  const navigate = useNavigate();
  const session = useSession();
  const office = useOfficeScope();
  const privacy = usePrivacy();
  const patient = useCurrentPatient();
  const isMac = isMacPlatform();
  // Menus and toolbars follow actions and commands as screens register them.
  const actions = useSyncExternalStore(
    actionRegistry.subscribe,
    actionRegistry.get,
    actionRegistry.get,
  );
  const commands = useSyncExternalStore(
    commandRegistry.subscribe,
    commandRegistry.get,
    commandRegistry.get,
  );

  const ctx = useMemo<ChromeContext>(() => {
    const allowed = (permission: string | undefined) =>
      !permission || canInScope(session, permission, office.locationIds);
    // A verb the person's role may not use in ANY office is hidden rather
    // than greyed: a greyed Claims or Reports button they can never use
    // reads as broken (familiar-nav audit 2026-10-29). Allowed in another
    // office: kept greyed, the reason says to switch office.
    const everyOffice = office.offices.map((o) => o.id);
    const allowedSomewhere = (permission: string) => canInScope(session, permission, everyOffice);
    const keysFor = (id: string) => skin.keymap.find((b) => b.action === id)?.keys;
    const toResolved = (id: string, inactive = false): ResolvedAction | undefined => {
      const a = findAction(id);
      if (!a) return undefined;
      const state = actionState(a, (p) => allowed(p));
      if (state.active === inactive) return undefined;
      if (
        !state.active &&
        a.enabled !== false &&
        a.permission &&
        !allowed(a.permission) &&
        !allowedSomewhere(a.permission)
      ) {
        return undefined;
      }
      const keys = keysFor(id);
      return {
        id,
        label: a.labelKey ? t(a.labelKey) : a.label,
        ...(a.icon ? { icon: <Icon name={a.icon} size={18} /> } : {}),
        ...(keys ? { shortcut: shortcutLabels(keys, isMac).join('+') } : {}),
        ...(state.active ? {} : { disabledReason: state.reason }),
        run: a.run,
      };
    };
    return {
      nav,
      moduleOrder: chrome.moduleBar.modules,
      action: (id) => toResolved(id),
      inactiveAction: (id) => toResolved(id, true),
      // Familiar modes grey what does not apply, with the reason (P1-20).
      showDisabled: greysDisabledActions(skin.skin) && chrome.toolbars.showDisabled !== false,
      commandsInGroup: (group) =>
        commands
          .filter((c) => c.group === group && allowed(c.permission))
          .map((c) => ({ id: c.id, label: c.title, run: c.run })),
      term: t,
      navigate: (href) => void navigate({ to: href }),
    };
    // `actions` re-resolves when a screen registers or drops a verb.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nav,
    chrome,
    commands,
    actions,
    session,
    office.locationIds,
    office.offices,
    skin.keymap,
    skin.skin,
    t,
    isMac,
    navigate,
  ]);

  // Re-render when any counter changes (menu counts, toolbar badges).
  useCounterValue(undefined);
  // Shell counters every mode can name: unread alerts and each nav item's badge.
  const unread = useSyncExternalStore(
    notifications.subscribe,
    notifications.unread,
    notifications.unread,
  );
  useEffect(() => registerCounter('shell.notifications', unread), [unread]);
  const badgeKey = nav.map((n) => `${n.id}=${n.badge ?? 0}`).join('|');
  useEffect(() => {
    const offs = nav.map((n) => registerCounter(`nav:${n.id}`, n.badge ?? 0));
    return () => {
      for (const off of offs) off();
    };
    // `badgeKey` captures every nav badge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [badgeKey]);
  const prefs = usePrefs();
  const [customizing, setCustomizing] = useState(false);
  const PatientStripSlot = useSkinSlot('shell.patientStrip');
  const ModuleBarFooterSlot = useSkinSlot('shell.moduleBarFooter');

  const moduleKey = activeModuleId ?? '';
  const menus = (chrome.menus.byModule?.[moduleKey] ?? chrome.menus.default).map((m, i) => ({
    id: m.id,
    label: labelText(m.label, t),
    items: resolveMenuItems(m.items, ctx, `menu-${i}`),
    ...(m.count ? { count: counterValue(m.count.counter) } : {}),
    ...(m.count?.emphasize ? { emphasize: true } : {}),
  }));
  const toolbarKey = chrome.toolbars.byModule?.[moduleKey] ? moduleKey : 'default';
  const modeToolbar = resolveToolbar(
    chrome.toolbars.byModule?.[moduleKey] ?? chrome.toolbars.default,
    ctx,
    renderSkinIcon,
  );
  const layout = chrome.customizableToolbar
    ? prefs.toolbarLayouts[skin.skin]?.[toolbarKey]
    : undefined;
  const toolbarItems = applyToolbarLayout(modeToolbar, layout).map((item) => {
    if ('separator' in item) return item;
    const { badge, ...rest } = item;
    return badge ? { ...rest, badge: counterValue(badge.counter) } : rest;
  });
  const saveLayout = (next: ToolbarLayoutValue | null) => {
    const others = Object.entries(prefs.toolbarLayouts[skin.skin] ?? {}).filter(
      ([key]) => key !== toolbarKey,
    );
    const forSkin = Object.fromEntries(next ? [...others, [toolbarKey, next]] : others);
    prefs.set({ toolbarLayouts: { ...prefs.toolbarLayouts, [skin.skin]: forSkin } });
  };

  const { primary, extras } = splitModules(nav, chrome.moduleBar.modules);
  const tiles = chrome.moduleBar.tiles ?? {};
  const toBar = (n: NavEntry) => {
    const tile = tiles[n.id] ?? tiles[n.moduleId];
    return {
      id: n.id,
      label: tile?.label ? labelText(tile.label, t) : n.label,
      icon: tile?.icon ? renderSkinIcon(tile.icon) : n.icon,
      href: tileHref(chrome, n.id, n.moduleId, n.href, patient?.id ?? null),
      ...(n.badge ? { badge: n.badge } : {}),
      ...(n.locked ? { locked: true } : {}),
    };
  };
  const chromeCtx: SkinChromeContext = {
    product: PRODUCT.name,
    moduleId: activeModuleId,
    title,
    user: {
      displayName: session.user.displayName,
      initials: initialsOf(session.user.displayName),
    },
    practiceName: session.practice.name,
    officeName: office.label,
    patient: patient
      ? {
          id: patient.id,
          label: privacy.mask(patient.label),
          chartNumber: patient.chartNumber,
          pmsId: patient.pmsId ?? null,
        }
      : null,
    today: new Date().toISOString().slice(0, 10),
  };
  const titleLine = titleText(chrome, chromeCtx);
  const patientLabel = patient
    ? `${privacy.mask(patient.label)}${patient.chartNumber ? ` [${patient.chartNumber}]` : ''}`
    : null;
  const stripActions = chrome.patientBar.actions
    ? resolveToolbar(chrome.patientBar.actions, ctx, renderSkinIcon)
    : [];
  const stripButtons = stripActions.filter((a): a is ResolvedToolbarButton => !('separator' in a));
  const iconStrip = chrome.patientBar.actionStyle === 'icons';
  const stripId = chrome.patientBar.showId
    ? (patient?.chartNumber ?? patient?.pmsId ?? '')
    : undefined;
  const defaultStrip = (
    <PatientStrip
      caption={`${t('patient')}:`}
      patient={chrome.patientBar.showId && patient ? privacy.mask(patient.label) : patientLabel}
      emptyText="None selected"
      selectLabel={t('selectPatient')}
      onSelect={() => ctx.action('patient.select')?.run()}
      {...(stripId !== undefined ? { patientId: stripId } : {})}
      actionStyle={iconStrip ? 'icons' : 'text'}
      actions={stripButtons.map((a) => (
        <button
          key={a.id}
          type="button"
          className={iconStrip ? 'vel-patientstrip__iconbtn' : 'vel-patientstrip__select'}
          {...(a.disabled
            ? {
                'aria-disabled': true,
                title: a.disabledReason ? `${a.label}: ${a.disabledReason}` : a.label,
                'data-disabled-reason': a.disabledReason ?? '',
                onClick: (e) => e.preventDefault(),
              }
            : { onClick: a.onSelect, ...(iconStrip ? { title: a.label } : {}) })}
          {...(iconStrip ? { 'aria-label': a.label } : {})}
          data-action={a.id}
        >
          {iconStrip ? a.icon : a.label}
        </button>
      ))}
    />
  );
  const statusCells = chrome.statusBar.format?.(chromeCtx);

  return (
    <ClassicShell
      titleStrip={
        chrome.titleStrip.show ? (
          <TitleStrip
            title={<span data-phi={chromeCtx.patient ? '' : undefined}>{titleLine}</span>}
            right={PRODUCT.name}
          />
        ) : undefined
      }
      menuBar={<MenuBar menus={menus} right={menuBarRight} />}
      toolbar={
        <IconToolbar
          aria-label={`${title} toolbar`}
          items={toolbarItems}
          labels={chrome.toolbars.labels}
          overflow={
            chrome.toolbars.overflowByModule?.[moduleKey] ?? chrome.toolbars.overflow ?? 'wrap'
          }
          {...(chrome.customizableToolbar
            ? {
                onContextMenu: (e) => {
                  e.preventDefault();
                  setCustomizing(true);
                },
              }
            : {})}
          right={
            <PaletteTrigger
              onOpen={onPalette}
              shortcut={shortcutLabels('mod+k', isMac)}
              placeholder="Search or jump to..."
            />
          }
        />
      }
      moduleBar={
        chrome.moduleBar.placement === 'none' ? undefined : (
          <ModuleBar
            items={primary.map(toBar)}
            extras={chrome.moduleBar.extras === 'bar' ? extras.map(toBar) : []}
            {...(activeId ? { activeId } : {})}
            orientation={chrome.moduleBar.placement === 'left' ? 'vertical' : 'horizontal'}
            labels={chrome.moduleBar.labels}
            renderLink={renderLink}
            {...(ModuleBarFooterSlot
              ? {
                  footer: (
                    <SkinSlot
                      name="shell.moduleBarFooter"
                      model={() => ({ extras: extras.map(toBar), navigate: ctx.navigate })}
                    >
                      {null}
                    </SkinSlot>
                  ),
                }
              : {})}
          />
        )
      }
      moduleBarPlacement={chrome.moduleBar.placement}
      patientBar={
        chrome.patientBar.show ? (
          PatientStripSlot ? (
            <SkinSlot
              name="shell.patientStrip"
              model={() => ({
                patient: chromeCtx.patient,
                actions: stripButtons.map((a) => ({
                  id: a.id,
                  label: a.label,
                  icon: a.icon,
                  run: a.onSelect,
                  ...(a.disabled ? { disabled: true, disabledReason: a.disabledReason ?? '' } : {}),
                })),
                select: () => ctx.action('patient.select')?.run(),
              })}
            >
              {defaultStrip}
            </SkinSlot>
          ) : (
            defaultStrip
          )
        ) : undefined
      }
      patientBarPosition={chrome.patientBar.position}
      statusBar={
        chrome.statusBar.show ? (
          <StatusBar>
            {statusCells ? (
              statusCells.map((cell, i) => <span key={i}>{cell}</span>)
            ) : (
              <>
                <span>{office.label}</span>
                <span>{session.user.displayName}</span>
                <span>{SKIN_LABELS[skin.skin]}</span>
                {/* Velora state with no menu-bar chrome: elapsed time, automations off. */}
                <TimeclockStatusText />
                <AutomationsStatusText />
              </>
            )}
          </StatusBar>
        ) : undefined
      }
    >
      {children}
      {chrome.customizableToolbar ? (
        <CustomizeToolbar
          open={customizing}
          onOpenChange={setCustomizing}
          buttons={modeToolbar.filter((i): i is ResolvedToolbarButton => !('separator' in i))}
          layout={layout}
          onSave={saveLayout}
        />
      ) : null}
    </ClassicShell>
  );
}
